import moment from 'moment';
import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import {reduxForm, formValueSelector, reset} from 'redux-form';
import classNames from 'classnames';

import ErrorBlock from 'components/ErrorBlock';

import {
  BookingPeriodSection,
  bookingPeriodInitialValues,
  RoomSection,
  roomInitialValues,
  RoomTypeSection,
  roomTypeInitialValues,
  CleanupPeriodSection,
  cleanupPeriodInitialValues,
  RentAndDepositSection,
  rentAndDepositInitialValues,
  BookingSection,
  bookingInitialValues,
  TenantsSection,
  tenantsInitialValues
} from './components';

import {formatDateForApi, DATE_FORMAT} from 'utils/datetime';
import {
  prepareTenants,
  checkSelectedBookingRoomTypeIsDifferent,
  checkSelectedBookingRoomIsDifferent
} from './utils';

import {createBookingExtension} from 'ducks/bookings';
import {
  fetchAvailableRoomsForBookingExtension,
  availableRoomsForBookingExtensionAreLoading
} from 'ducks/rooms';
import {
  selectModal,
  storeDataInModal,
  MODALS as BASE_MODALS
} from 'ducks/modals';
import {checkForNotMatchingRoomPurposesToBookingType} from 'ducks/allocations/utils';

import {fetchBookingOptions as sdkFetchBookingOptions} from 'sdk/bookings';

import './styles.css';

const formSubmit = (values, _dispatch, props) => {
  const booking = _.get(values, 'booking.object');

  const {primaryTenant, secondaryTenant} = prepareTenants(values);

  const data = {
    existingBooking: _.get(booking, 'id'),
    room: _.get(values, 'room.value'),
    roomType: _.get(values, 'roomType.object.id'),
    startDate: formatDateForApi(values.startDate),
    endDate: formatDateForApi(values.endDate),
    primaryTenant,
    secondaryTenant,
    pwRent: values.pwRent && parseFloat(parseFloat(values.pwRent).toFixed(2)),
    roomDeposit:
      values.roomDeposit &&
      parseFloat(parseFloat(values.roomDeposit).toFixed(2)),
    createCleanupPeriod: values.createCleanup,
    cleanupPeriodDays: values.cleanupPeriodDays
  };

  props.createBookingExtension(data, props.getExtraActions());
};

class BookingExtensionCreateForm extends React.Component<any> {
  onRoomTypeChange = roomType => {
    const {endDate, createCleanupValue, cleanupPeriodDaysValue} = this.props;

    // resetSection resets the field to be pristine again;
    // In this way when fethching data again, the first room from the newly fetched rooms will be set as initial value
    this.props.resetSection('room');
    this.fetchAvailableRooms({
      endDate,
      roomType,
      cleanupPeriodDays: createCleanupValue ? cleanupPeriodDaysValue : null
    });
  };

  onCleanupPeriodDaysChange = event => {
    const days = event.target.value;
    if (days === '' || days === '0') return;

    const {endDate, roomType, createCleanupValue} = this.props;

    this.fetchAvailableRooms({
      endDate,
      roomType,
      cleanupPeriodDays: createCleanupValue ? days : null
    });
  };

  onCleanupPeriodCheckboxChange = event => {
    const checked = event.target.checked;

    const {endDate, roomType, cleanupPeriodDaysValue} = this.props;

    this.fetchAvailableRooms({
      endDate,
      roomType,
      cleanupPeriodDays: checked ? cleanupPeriodDaysValue : null
    });
  };

  fetchAvailableRooms = ({endDate, roomType, cleanupPeriodDays}) => {
    const booking = _.get(this.props, 'booking.id');
    // The method receives cleanupPeriodDays, endDate and roomType
    // rather than taking them from this.props because it is invoked on change
    if ([endDate, roomType, booking].some(_.isNil)) return;

    const locationRef = this.props.locationRef;
    const extraActions = this.props.storeDataExtraActions(data => ({
      bookingExtensionAvailableRooms: data.rooms,
      roomAvailabilityMessage: data.message
    }));
    this.props.fetchAvailableRoomsForBookingExtension(
      {
        locationRef,
        booking,
        roomType: roomType.value,
        extensionEnd: formatDateForApi(endDate),
        extensionCleanupPeriodDays: cleanupPeriodDays
      },
      extraActions
    );
  };

  onEndDateChange = endDate => {
    const {roomType, createCleanupValue, cleanupPeriodDaysValue} = this.props;

    this.fetchAvailableRooms({
      endDate,
      roomType,
      cleanupPeriodDays: createCleanupValue ? cleanupPeriodDaysValue : null
    });
  };

  fetchBookingOptions = search => {
    const {locationRef, initialBookingSet} = this.props;
    if (initialBookingSet) {
      // Imitate response from API
      return new Promise(resolve =>
        resolve({
          data: {
            results: initialBookingSet.bookings
              .filter(booking => !booking.cancelled)
              .map(booking => ({
                ...booking,
                period: {startDate: booking.startDate, endDate: booking.endDate}
              }))
          }
        })
      );
    }

    return sdkFetchBookingOptions({locationRef, search});
  };

  clearBookingOption = () => {
    this.props.reset('createBookingExtension');

    this.props.storeDataInModal({
      modalName: BASE_MODALS.CREATE_BOOKING,
      data: {
        bookingExtensionAvailableRooms: [],
        roomAvailabilityMessage: ''
      }
    });
  };

  render() {
    const {
      error,
      booking,
      showForm,
      roomTypes,
      availableRooms,
      noAvailableRooms,
      createCleanupValue,
      differentRoomType,
      differentRoom,
      roomAvailabilityMessage,
      disablePrimaryTenantRemoval,
      roomPurposesForPeriod,
      selectedBookingType,
      availableRoomsForBookingExtensionAreLoading,
      startDate,
      endDate
    } = this.props;

    const startDateGreaterThаnEndDate =
      startDate &&
      endDate &&
      moment(startDate, DATE_FORMAT) > moment(endDate, DATE_FORMAT);

    const hasNotMatchingRoomPurposeToBookingType = checkForNotMatchingRoomPurposesToBookingType(
      {
        roomPurposesForPeriod,
        bookingType: selectedBookingType
      }
    );

    return (
      <form
        className={classNames('BookingExtensionCreateForm', {
          hidden: !showForm
        })}>
        {error && <ErrorBlock>{error}</ErrorBlock>}
        <BookingSection
          fetchBookingOptions={this.fetchBookingOptions}
          clearBookingOption={this.clearBookingOption}
        />

        {booking && (
          <TenantsSection
            primaryTenant={booking.primaryTenant}
            secondaryTenant={booking.secondaryTenant}
            disablePrimaryTenantRemoval={disablePrimaryTenantRemoval}
          />
        )}

        <BookingPeriodSection
          onEndChange={this.onEndDateChange}
          startDateGreaterThаnEndDate={startDateGreaterThаnEndDate}
        />

        <CleanupPeriodSection
          createCleanupValue={createCleanupValue}
          onCleanupPeriodCheckboxChange={this.onCleanupPeriodCheckboxChange}
          onCleanupPeriodDaysChange={this.onCleanupPeriodDaysChange}
        />

        <RoomTypeSection
          roomTypes={roomTypes}
          onRoomTypeChange={this.onRoomTypeChange}
          differentRoomType={differentRoomType}
        />

        <RoomSection
          availableRooms={availableRooms}
          noAvailableRooms={noAvailableRooms}
          differentRoom={differentRoom}
          hasNotMatchingRoomPurposeToBookingType={
            hasNotMatchingRoomPurposeToBookingType
          }
          roomAvailabilityMessage={roomAvailabilityMessage}
          availableRoomsAreLoading={availableRoomsForBookingExtensionAreLoading}
        />

        <RentAndDepositSection />
      </form>
    );
  }
}

const formSelector = formValueSelector('createBookingExtension');

const mapStateToProps = state => {
  // TODO: Do not use "modal" data in this function since the form
  // should be available for use outside this modal
  const modal = selectModal(state, BASE_MODALS.CREATE_BOOKING);

  const availableRooms = _.get(
    modal,
    'data.bookingExtensionAvailableRooms',
    []
  );

  const initialBookingSet = _.get(modal, 'data.initialBookingSet');

  const roomType = formSelector(state, 'roomType');
  const noAvailableRooms = _.isEmpty(availableRooms) && roomType;
  const selectedBookingValue = formSelector(state, 'booking');
  const booking = _.get(selectedBookingValue, 'object');

  const selectedBooking = formSelector(state, 'booking.object');
  const selectedRoomType = formSelector(state, 'roomType');
  const selectedRoom = formSelector(state, 'room');

  const differentRoomType = checkSelectedBookingRoomTypeIsDifferent({
    selectedBooking,
    selectedRoomType
  });

  const differentRoom = checkSelectedBookingRoomIsDifferent({
    selectedBooking,
    selectedRoom
  });
  const endDate = formSelector(state, 'endDate');
  const startDate = formSelector(state, 'startDate');

  return {
    initialValues: {
      ...cleanupPeriodInitialValues(),
      ...bookingPeriodInitialValues(state),
      ...roomTypeInitialValues(state),
      ...rentAndDepositInitialValues(state),
      ...roomInitialValues(state),
      ...tenantsInitialValues(),
      ...bookingInitialValues({initialBookingSet})
    },
    endDate,
    startDate,
    roomType,
    booking,
    createCleanupValue: formSelector(state, 'createCleanup'),
    cleanupPeriodDaysValue: formSelector(state, 'cleanupPeriodDays'),
    availableRooms,
    noAvailableRooms,
    differentRoomType,
    differentRoom,
    initialBookingSet,
    disablePrimaryTenantRemoval: !_.isNil(initialBookingSet),
    roomAvailabilityMessage: _.get(modal, 'data.roomAvailabilityMessage', ''),
    roomPurposesForPeriod: _.get(
      formSelector(state, 'room'),
      'object.roomPurposesForPeriod',
      []
    ),
    selectedBookingType: _.get(booking, 'bookingType'),
    availableRoomsForBookingExtensionAreLoading: availableRoomsForBookingExtensionAreLoading(
      state
    )
  };
};

const mapDispatchToProps = {
  reset,
  fetchAvailableRoomsForBookingExtension,
  createBookingExtension,
  storeDataInModal
};

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: 'createBookingExtension',
    onSubmit: formSubmit,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true
  })(BookingExtensionCreateForm)
);
